import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ChevronUpIcon, XIcon, PhoneIcon } from 'lucide-react';
import { FunnelProvider, STEPS, STEP_ESTIMATE, STEP_RESERVE, STEP_STATUS, useFunnel } from '../contexts/FunnelContext';
import { FunnelHeader } from '../components/funnel/FunnelHeader';
import { HomeLanding } from '../components/funnel/HomeLanding';
import { ProgressBar } from '../components/funnel/ProgressBar';
import { StepSize } from '../components/funnel/StepSize';
import { StepAddresses } from '../components/funnel/StepAddresses';
import { StepAccess } from '../components/funnel/StepAccess';
import { StepInventory } from '../components/funnel/StepInventory';
import { StepPacking } from '../components/funnel/StepPacking';
import { StepDate } from '../components/funnel/StepDate';
import { StepContact } from '../components/funnel/StepContact';
import { StepEstimate } from '../components/funnel/StepEstimate';
import { StepReserve } from '../components/funnel/StepReserve';
import { StepStatus } from '../components/funnel/StepStatus';
import { QuoteSummary } from '../components/funnel/QuoteSummary';
import { stepErrors, type StepErrors } from '../utils/validation';
import { hrs } from '../utils/format';
import { brand, phones, siteConfig } from '../data/siteConfig';
import { firePageView } from '../utils/metaPixel';
const completionEvents: Record<number, string> = {
  1: 'AddressCompleted',
  2: 'AccessCompleted',
  3: 'InventoryCompleted'
};
function FunnelFlow() {
  const {
    step,
    goTo,
    data,
    t,
    track,
    quote,
    submitted,
    acceptEstimate
  } = useFunnel();
  const [attempted, setAttempted] = useState<Record<number, boolean>>({});
  const [sheet, setSheet] = useState(false);
  const errors: StepErrors = attempted[step] ? stepErrors(step, data) : {};
  const showNav = step <= STEP_ESTIMATE;
  const next = () => {
    const e = stepErrors(step, data);
    if (Object.keys(e).length) {
      setAttempted(a => ({
        ...a,
        [step]: true
      }));
      return;
    }
    if (completionEvents[step]) track(completionEvents[step], {
      step
    });
    if (step === STEP_ESTIMATE) acceptEstimate();
    goTo(step + 1);
  };
  const content = [<StepSize key="s" errors={errors} data-id="element-1130" data-mp="3igch32mnr9or" />, <StepAddresses key="a" errors={errors} data-id="element-1131" data-mp="3djdq2syajius" />, <StepAccess key="ac" data-id="element-1132" data-mp="3dhj1n4nt98ly" />, <StepInventory key="i" errors={errors} data-id="element-1133" data-mp="2ptrra6ltz5nb" />, <StepPacking key="p" errors={errors} data-id="element-1134" data-mp="2pukpukamyoud" />, <StepDate key="d" errors={errors} data-id="element-1135" data-mp="2lsg0hxr80edj" />, <StepContact key="c" errors={errors} data-id="element-1136" data-mp="2lth9zxsl8st5" />, <StepEstimate key="e" data-id="element-1137" data-mp="2lqmp655nx5z7" />, <StepReserve key="r" data-id="element-1138" data-mp="2zke1tmeox5z5" />, <StepStatus key="st" data-id="element-1139" data-mp="2zkmf3feq6dav" />][step];
  return <>
      <ProgressBar data-id="element-1140" data-mp="2ute5ojdearxp" />
      <div className="mx-auto grid max-w-6xl gap-10 px-4 pb-32 pt-8 sm:px-6 lg:grid-cols-[1fr_320px] lg:pb-16 lg:pt-10" data-id="element-1141" data-mp="2usvy4qla8rah">
        <main data-id="element-1142" data-mp="2728nvynw9pkr">
          <AnimatePresence mode="wait" initial={false} data-id="element-1143" data-mp="271qm6migqj5g">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -12
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-1144" data-mp="2258k5ueioywt">
              {content}
            </motion.div>
          </AnimatePresence>

          {showNav && <div className="fixed inset-x-0 bottom-0 z-20 border-t border-ink-200 bg-white/95 px-4 py-3 backdrop-blur lg:static lg:mt-12 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0" data-id="element-1145" data-mp="8m9lt3rw72gn">
              <div className="mx-auto flex max-w-6xl items-center gap-3" data-id="element-1146" data-mp="119vom9taq32g">
                {step > 0 && <button type="button" onClick={() => goTo(step - 1)} className="flex h-14 items-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 font-semibold text-ink transition-[border-color] duration-150 ease-out hover:border-ink" data-id="element-1147" data-mp="15cihme1pdu18">
                    <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1148" data-mp="3c0vigl6t3oao" />
                    <span className="hidden sm:inline" data-id="element-1149" data-mp="3h1t9ln9snsob">{t('back')}</span>
                  </button>}
                {data.bedrooms && <button type="button" onClick={() => setSheet(true)} className="hidden h-14 flex-col items-start justify-center rounded-xl bg-ink-100 px-3 text-left min-[420px]:flex lg:hidden" aria-label={t('summary')} data-id="element-1150" data-mp="7n2i6bavtjy">
                    <span className="flex items-center gap-1 text-[11px] font-medium text-ink-500" data-id="element-1151" data-mp="3ik1p79te0m6f">
                      {t('summary')} <ChevronUpIcon className="h-3 w-3" aria-hidden="true" data-id="element-1152" data-mp="j4xztlddgcwr" />
                    </span>
                    <span className="whitespace-nowrap text-sm font-bold text-ink" data-id="element-1153" data-mp="f2t5slj0he9l">
                      {quote.crew.used} · {hrs(quote.serviceLow)}–{hrs(quote.serviceHigh)}h
                    </span>
                  </button>}
                <a href={phones.ai.href} aria-label={`${t('callKyle')} ${phones.ai.display}`} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-ink-200 bg-white text-ink lg:hidden" data-id="element-1154" data-mp="56zm8i6vkkib">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1155" data-mp="2fp2winpdtpc0" />
                </a>
                <button type="button" onClick={next} className="flex h-14 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-magenta px-5 text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] sm:text-[19px] lg:max-w-sm" data-id="element-1156" data-mp="2kqjw8fw4r5r0">
                  {step === STEP_ESTIMATE ? t('acceptContinue') : t('next')}
                  <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1157" data-mp="38h64a764wq10" />
                </button>
              </div>
            </div>}
          {step === STEP_RESERVE && <button type="button" onClick={() => goTo(step - 1)} className="mt-6 inline-flex min-h-[44px] items-center gap-2 font-semibold text-ink underline decoration-ink-300 underline-offset-4" data-id="element-1158" data-mp="1e2lkrxtjv0j7">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1159" data-mp="1azflh70fpjo8" />
              {t('back')}
            </button>}
        </main>
        <aside className="hidden lg:block" data-id="element-1160" data-mp="1638mjy6gh8fs">
          <div className="sticky top-24" data-id="element-1161" data-mp="1kyy2pfoymrcl">
            <QuoteSummary onEdit={submitted || step === STEP_STATUS ? undefined : goTo} data-id="element-1162" data-mp="1kzpx4n6tmj1a" />
          </div>
        </aside>
      </div>

      <AnimatePresence data-id="element-1163" data-mp="1tz38xmif3jp6">
        {sheet && <motion.div className="fixed inset-0 z-40 flex items-end bg-ink/50 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={() => setSheet(false)} data-id="element-1164" data-mp="1txs5jnsvwdyl">
            <motion.div role="dialog" aria-modal="true" aria-label={t('summary')} className="w-full p-3" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-1165" data-mp="23o4l11ludgxv">
              <div className="relative" data-id="element-1166" data-mp="2wb4cyax1xgrj">
                <button type="button" onClick={() => setSheet(false)} aria-label={t('close')} className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-ink-100" data-id="element-1167" data-mp="2wa32a7jy6wac">
                  <XIcon className="h-5 w-5" data-id="element-1168" data-mp="2n8v37n9lxrf7" />
                </button>
                <QuoteSummary onEdit={s => {
              setSheet(false);
              goTo(s);
            }} data-id="element-1169" data-mp="28kqtv9wg2s93" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function FunnelShell() {
  const {
    started,
    t
  } = useFunnel();
  return <div className="min-h-screen w-full bg-paper" data-id="element-1170" data-mp="23kcnrxeelsdp">
      <FunnelHeader data-id="element-1171" data-mp="1po8uq7f9nq2t" />
      {started ? <FunnelFlow data-id="element-1172" data-mp="1pp0odcpazdqg" /> : <HomeLanding data-id="element-1173" data-mp="1um0vzs1swojo" />}
      <footer className="border-t border-ink-200 bg-paper" data-id="element-1174" data-mp="1umk30dpwxsix">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-ink-500 sm:px-6" data-id="element-1175" data-mp="1uo3nyhmikrr1">
          <div data-id="element-1176" data-mp="254fksoa27vv5">
            <p data-id="element-1177" data-mp="2560eycsgpcsh">© 2026 {brand.name} · {t('footerScope')}</p>
            <p className="mt-1" data-id="element-1178" data-mp="213c3ryg9ztsc">
              {t('footerLegal')}
              {siteConfig.privacyUrl && <>
                  {' · '}
                  <a href={siteConfig.privacyUrl} className="underline underline-offset-4 hover:text-ink" data-id="element-1179" data-mp="1w3r4to13vlfm">Privacy Policy</a>
                </>}
              {siteConfig.smsTermsUrl && <>
                  {' · '}
                  <a href={siteConfig.smsTermsUrl} className="underline underline-offset-4 hover:text-ink" data-id="element-1180" data-mp="1hf3d5sudrvuy">SMS Terms</a>
                </>}
            </p>
          </div>
          <Link to="/admin" className="font-medium text-ink-600 underline underline-offset-4 hover:text-ink" data-id="element-1181" data-mp="1dbyczwxrw69h">
            {t('footerStaff')}
          </Link>
        </div>
      </footer>
    </div>;
}
export function FunnelPage({
  creativeKey
}: {
  creativeKey: string;
}) {
  useEffect(() => {
    firePageView();
  }, []);
  const urlKey = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('creative_key') : null;
  return <FunnelProvider creativeKey={urlKey ?? creativeKey} data-id="element-1182" data-mp="3p2fxmfuzl887">
      <FunnelShell data-id="element-1183" data-mp="3t2q55wyfg6p6" />
    </FunnelProvider>;
}
export { STEPS };